@use "@styles/_variables/colors" as *;
@use "@styles/_variables/breakpoints" as *;
@use "@styles/_variables/sizes" as *;
@use "@styles/_variables/mixins" as *;
@use "@styles/_variables/typography" as *;

.illustrated-text-list {
  display: grid;
  grid-gap: $tab-quadruple;
  margin-top: $tab-double;

  .row {
    margin-left: $header-logo-size + $header-logo-spacing;
    margin-right: $header-logo-size + $header-logo-spacing;

    // Eyebrow above the row title. The row already carries the gutter margin,
    // so the kicker sits flush with the h4 and the card underneath it.
    .kicker {
      @include kicker();
      margin-bottom: 1rem;
    }

    // User-tuned baseline: journey headings share the section-title scale.
    h4 {
      position: relative;
      margin-bottom: $tab;
      font-family: $font-display-bold;
      font-size: $section-title-font-size;
      color: $white;
      @include sciFiTitleGlow();
    }

    .text-body {
      @include technoCard();
      @include cardProse();
      // Contains the floated media panel so the card ring hugs it, and
      // anchors the ghost numeral.
      display: flow-root;
      position: relative;

      // Journey panels share the row with long copy, so they take less of
      // the card than the Home floats.
      .frame {
        --m-w: 50%;
      }

      // The row's ghost numeral: a catalogue watermark in the card's bottom
      // outer corner, behind the copy. One per row, so no two cards close on
      // the same detail.
      &::after {
        content: attr(data-numeral);
        position: absolute;
        right: 2.4rem;
        bottom: 0.6rem;
        z-index: 0;
        font-family: $font-display-bold;
        font-size: 9rem;
        line-height: 1;
        letter-spacing: 0.06em;
        color: rgba($gold, 0.06);
        pointer-events: none;
        user-select: none;
      }

      p,
      ul {
        font-size: $card-text-font-size;
        margin-bottom: 0;
      }

      // Rhythm only: the steel hairline that used to rule off every paragraph
      // competed with the card's own ring for attention.
      p + p {
        margin-top: $tab;
      }

      // Scoped to the card rather than to <p>: leads and list items carry
      // links too, and they should all read the same.
      a {
        color: $url;
        text-decoration: none;
        &:hover {
          color: $url-hoover;
          text-shadow: $text-glow-strong;
        }
        &:active {
          color: $url-hoover;
          text-shadow: $text-glow-strong;
        }
        &:visited {
          color: $url;
        }
      }
    }

    // Every copy block past the row's declared beside-budget CLEARS below
    // the panel: a paragraph either sits fully beside the art (opening at
    // its top, where lines have room) or fully below it — never opening in
    // the one-line sliver at the panel's tail, at any viewport width.
    &.row-beside-1 .text-body {
      p,
      ul {
        clear: both;
      }
    }

    &.row-beside-2 .text-body {
      p:nth-of-type(n + 2),
      ul {
        clear: both;
      }
    }

    &.row-beside-3 .text-body {
      p:nth-of-type(n + 3),
      ul {
        clear: both;
      }
    }

    // Mirrored rows float the panel right; the numeral takes the opposite
    // outer corner.
    &.row-mirror .text-body::after {
      right: auto;
      left: 2.4rem;
    }

    // Rows with shorter beside-copy hand more of the card to the art.
    &.row-wide-art .text-body .frame {
      --m-w: 54%;
    }
  }

  @media all and (max-width: $mobile-large) {
    grid-gap: $tab-quadruple-mobile-large;

    .row {
      margin-left: 0;
      margin-right: 0;

      .kicker {
        margin-bottom: 0.8rem;
        font-size: $kicker-font-size-mobile-large;
      }

      h4 {
        margin-bottom: $tab-mobile-large;
        font-size: $section-title-font-size-mobile-large;
      }

      .text-body {
        @include technoCardMobile();

        // Panels stack full-width on a phone; the ghost numeral is desktop
        // furniture with no corner to sit in.
        &::after {
          content: none;
        }

        p,
        ul {
          font-size: $card-text-font-size-mobile-large;
        }

        blockquote {
          padding-left: $tab-mobile-large;
          font-size: $card-lead-font-size-mobile-large;
          margin-bottom: $tab-double-mobile-large;
        }

        p + blockquote,
        ul + blockquote {
          margin-top: $tab-double-mobile-large;
        }
      }
    }
  }

  @media all and (max-width: $mobile-small) {
    grid-gap: $tab-quadruple-mobile-small;

    .row {
      .kicker {
        font-size: $kicker-font-size-mobile-small;
      }

      h4 {
        margin-bottom: $tab-mobile-small;
        font-size: $section-title-font-size-mobile-small;
      }

      .text-body {
        p,
        ul {
          font-size: $card-text-font-size-mobile-small;
        }

        blockquote {
          padding-left: $tab-mobile-small;
          font-size: $card-lead-font-size-mobile-small;
          margin-bottom: $tab-double-mobile-small;
        }

        p + blockquote,
        ul + blockquote {
          margin-top: $tab-double-mobile-small;
        }
      }
    }
  }
}
